/**
 * VÉRAULT Member Suite — Timezone utilities (Wave 30 · Phase 7d)
 * ───────────────────────────────────────────────────────────────
 * Single source of truth for trip-local time math.
 *
 * Reads the trip's `destination_timezone` field (IANA — set by the
 * advisor on trip creation; defaults to "America/New_York" upstream).
 *
 * Built on Intl.DateTimeFormat — no external dependency, no clock
 * drift surprises across DST. Works in every browser back to iOS 14.
 */

/**
 * Resolve a trip's local IANA timezone.
 * @param {object|null|undefined} trip
 * @returns {string} IANA TZ (always returns a non-empty value)
 */
export function getTripTimezone(trip) {
  if (!trip) return "UTC";
  return (
    trip.destination_timezone ||
    trip.default_timezone ||
    trip.reveal_timezone ||
    trip.timezone ||
    "UTC"
  );
}

/**
 * Format a moment in the trip's local time.
 * @param {Date|string|number} when
 * @param {string} timezone IANA TZ
 * @param {object} options Intl.DateTimeFormat options (default: H:MM)
 * @returns {string}
 */
export function formatTripLocalTime(when, timezone, options) {
  const d = when instanceof Date ? when : new Date(when);
  if (Number.isNaN(d.getTime())) return "";
  const opts = options || { hour: "numeric", minute: "2-digit", hour12: true };
  try {
    return new Intl.DateTimeFormat("en-US", { ...opts, timeZone: timezone }).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-US", opts).format(d);
  }
}

/**
 * Get the short timezone abbreviation (EST · BST · JST) for the trip TZ
 * at a given moment. Falls back to GMT offset (UTC+5) when the runtime
 * doesn't know the short name (rare).
 */
export function getTimezoneAbbreviation(timezone, when) {
  const d = when instanceof Date ? when : new Date(when || Date.now());
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    }).formatToParts(d);
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    return tzPart ? tzPart.value : "";
  } catch {
    return "";
  }
}

/**
 * Compute today's ISO date (YYYY-MM-DD) in the trip's local timezone.
 * Used to drive "today" logic that respects where the traveler is
 * physically, not the server's UTC clock.
 */
export function getLocalDateISO(timezone, when) {
  const d = when instanceof Date ? when : new Date(when || Date.now());
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d);
    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const da = parts.find((p) => p.type === "day")?.value;
    if (y && m && da) return `${y}-${m}-${da}`;
  } catch {
    /* fall through to UTC */
  }
  return d.toISOString().slice(0, 10);
}

/**
 * useLocalTime — React hook returning the current trip-local time,
 * auto-refreshing every minute.
 *
 * Returns: { time: "07:42 AM", abbreviation: "EST", iso: "2026-06-02" }
 */
import { useEffect, useState } from "react";

export function useLocalTime(timezone) {
  const compute = () => ({
    time: formatTripLocalTime(new Date(), timezone),
    abbreviation: getTimezoneAbbreviation(timezone),
    iso: getLocalDateISO(timezone),
  });
  const [snapshot, setSnapshot] = useState(compute);

  useEffect(() => {
    if (!timezone) return undefined;
    setSnapshot(compute());
    // Re-tick every 30s so the displayed time stays accurate.
    const t = window.setInterval(() => setSnapshot(compute()), 30000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezone]);

  return snapshot;
}
